/**
 * useEditorDoc — per-site Yjs document lifecycle.
 *
 * Creates one Y.Doc per siteId, wires:
 *   - BroadcastChannel provider (T3.5) for cross-tab awareness
 *   - Y.UndoManager (T3.4) scoped to the maps y-xyflow and y-grid use
 *   - OPFS persistence (M4) — sqlite-wasm-backed local durability so
 *     edits survive a reload, via @fredericrous/yjs-sqlite-provider
 *
 * The doc is stored in a React ref so it survives re-renders without
 * re-creating. On unmount, all providers are destroyed.
 */
import { useEffect, useRef, useState } from "react";
import * as Y from "yjs";
import { LOCAL_ORIGIN as FLOW_ORIGIN } from "@website-builder/y-xyflow";
import {
  LOCAL_ORIGIN as GRID_ORIGIN,
  GRID_ITEMS_MAP,
  setGridDocSeeding,
} from "@website-builder/y-grid";
import type { SqliteProvider } from "@fredericrous/yjs-sqlite-provider";
import { WebsocketSyncProvider } from "@website-builder/yjs-bridge-provider";
import { getBridgeConfig, isCloudSiteId } from "./bridgeConfig.js";
import { getCollabConfig } from "./collabConfig.js";
import { LeaderCoordinator, type AppVersion } from "./leaderCoordinator.js";
import { putAsset } from "./assets/opfsAssetStore.js";
import { BroadcastProvider } from "./broadcastProvider.js";
import {
  registerCollabBlobBridge,
  unregisterCollabBlobBridge,
} from "./assets/collabBlobResolver.js";
import {
  markBackedUp,
  resetBackupStatus,
} from "./assets/collabBackupStatus.js";
import { setRoomPeerCount } from "./assets/collabPeers.js";
import { flushPendingEdits, setUndoBoundary } from "./pendingEdits.js";
import { renameClashingMovedInputs } from "./fieldNames.js";

// ── Hook ──────────────────────────────────────────────────────────

interface DocBundle {
  ydoc: Y.Doc;
  undoManager: Y.UndoManager;
}

export interface EditorDoc extends DocBundle {
  /**
   * False until local OPFS persistence has finished replaying the
   * stored doc (or been determined unavailable). The editor must NOT
   * mount its canvas/bindings until this is true: the y-xyflow / y-grid
   * bindings are late-joiners (they import the doc when it already has
   * data, else seed a fresh default). Mounting them before the async
   * replay completes would push the default sitemap into the doc and
   * race-clobber the persisted state. Gating on `loaded` guarantees the
   * bindings see the fully-replayed doc and import it.
   */
  loaded: boolean;
  /**
   * True only when THIS tab is the authoritative owner of the doc — the
   * persistence leader that finished its OPFS replay, or the sole owner when
   * persistence is unavailable. ONLY such a tab may create the default
   * new-site content (sitemap + header/footer); a FOLLOWER must never seed.
   *
   * Why it's separate from `loaded`: the follower bootstrap has a 3s anti-hang
   * fallback that flips `loaded` even if no peer ever answered the sync
   * handshake. If the seed sites keyed on `loaded` alone, that fallback would
   * let an unsynced follower seed a default sitemap into its empty doc and
   * broadcast it — merging duplicate/conflicting defaults into the real doc.
   * Gating seeding on `canSeed` closes that hole: an unsynced follower mounts
   * empty and waits for sync instead of seeding.
   */
  canSeed: boolean;
}

/** No-version fallback: all tabs equal → plain FIFO election, no version yield.
 *  The app supplies its real build version so the newest tab wins leadership. */
const DEFAULT_VERSION: AppVersion = { semver: "0.0.0", build: 0 };

export function useEditorDoc(
  siteId: string,
  version: AppVersion = DEFAULT_VERSION,
): EditorDoc {
  const docRef = useRef<DocBundle | null>(null);
  const providerRef = useRef<BroadcastProvider | null>(null);
  const persistRef = useRef<SqliteProvider | null>(null);
  const wsRef = useRef<WebsocketSyncProvider | null>(null);
  const [loaded, setLoaded] = useState(false);
  // Authoritative-owner flag — see EditorDoc.canSeed. Only a leader that
  // finished replay (or the sole owner sans persistence) may seed defaults.
  const [canSeed, setCanSeed] = useState(false);
  // Bumped on `hashchange` so the collab effect re-evaluates when a share link
  // is added to the URL in-session (e.g. the owner clicks "Share") — not just
  // on first load with a link present.
  const [collabEpoch, setCollabEpoch] = useState(0);

  if (!docRef.current) {
    const ydoc = new Y.Doc();

    // The maps y-xyflow and y-grid write into — scoped to this site.
    // UndoManager tracks changes originating from LOCAL_ORIGIN on
    // both maps; remote changes (other peers/tabs) are NOT undoable
    // by the local user. (T3.4)
    const yNodes = ydoc.getMap("nodes");
    const yEdges = ydoc.getMap("edges");
    // The flat single-parent item store (all grids' items). Item bodies +
    // cross-grid moves (a `::parent` re-point) are undoable, so it's in scope
    // from the start; per-grid `grid:<id>` maps (config/`::bp:`) are added
    // dynamically as grids are created (usePageNode / sharedRegions).
    const yGridItems = ydoc.getMap(GRID_ITEMS_MAP);

    const undoManager = new Y.UndoManager([yNodes, yEdges, yGridItems], {
      trackedOrigins: new Set<unknown>([FLOW_ORIGIN, GRID_ORIGIN]),
      captureTimeout: 500, // group rapid edits into one undo step
    });

    // Mark the doc's initial-hydration window: originator grid pushes during it
    // are non-undoable seed, but an "add section/widget" AFTER load undoes
    // cleanly (block + items together). Cleared once `loaded` (below).
    setGridDocSeeding(ydoc, true);

    docRef.current = { ydoc, undoManager };
  }

  // End the seeding window once the doc has hydrated. This effect (in the doc
  // PROVIDER) runs AFTER the canvas/PageNode child effects that do the initial
  // seed (React flushes child effects before parent), so every fresh-site seed
  // push already happened under SEED_ORIGIN; subsequent user adds are undoable.
  useEffect(() => {
    if (loaded) setGridDocSeeding(docRef.current!.ydoc, false);
  }, [loaded]);

  // Wire the BroadcastChannel provider on mount, tear down on unmount
  // or when siteId changes.
  useEffect(() => {
    const { ydoc } = docRef.current!;
    providerRef.current = new BroadcastProvider(ydoc, `site:${siteId}`, {
      // A follower tab (doesn't open OPFS) is ready once a peer hands it the
      // current doc; unblock the editor then.
      onSynced: () => setLoaded(true),
    });

    return () => {
      providerRef.current?.destroy();
      providerRef.current = null;
    };
  }, [siteId]);

  // Cross-device sync via the bridge — the WS provider BroadcastChannel can't be
  // (different browsers/devices). Config-gated: no-op until VITE_SYNC_BRIDGE_URL
  // is set, so today's behavior is unchanged. Own effect → never gates `loaded`.
  useEffect(() => {
    const cfg = getBridgeConfig();
    if (!cfg) return;
    // An authenticated bridge only admits cloud sites; a local draft has no
    // token to show it.
    if (cfg.requiresToken && !isCloudSiteId(siteId)) return;
    const { ydoc } = docRef.current!;
    wsRef.current = new WebsocketSyncProvider(ydoc, {
      url: cfg.url,
      siteId,
      ...(cfg.getToken ? { getToken: () => cfg.getToken!(siteId) } : {}),
      requireToken: cfg.requiresToken,
      // Only the OPFS leader persists, so only it has something to claim.
      // Remote writers (builder-mcp) wait for this before reporting "saved".
      persisted: () => persistRef.current?.persistedStateVector() ?? null,
      // Media a remote writer hands over (builder-mcp's upload_asset) lands in
      // the same OPFS store as a user's own upload; only the leader keeps it.
      acceptBlob: async ({ sha256, mime, bytes }) => {
        if (!persistRef.current) return false;
        const stored = await putAsset(
          siteId,
          new Blob([bytes as Uint8Array<ArrayBuffer>], { type: mime }),
        );
        return stored.sha256 === sha256;
      },
    });
    return () => {
      wsRef.current?.destroy();
      wsRef.current = null;
    };
  }, [siteId]);

  // Re-evaluate collaboration when the URL fragment changes in-session.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onHash = () => setCollabEpoch((e) => e + 1);
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  // Free-mode P2P collaboration — peers sync the doc directly (relay = only
  // server) and exchange image blobs, no login. Gated on getCollabConfig():
  // a deployed relay AND a `#room&k` share link in the URL; otherwise a no-op,
  // so default behaviour is unchanged. libp2p (+blob store) is lazy-imported
  // here so it never enters the main bundle until collaboration is active.
  // Own effect → never gates `loaded`; teardown stops the node.
  useEffect(() => {
    const cfg = getCollabConfig();
    if (!cfg) return;
    const { ydoc } = docRef.current!;

    let disposed = false;
    let teardown: (() => void) | undefined;

    void (async () => {
      try {
        const p2p = await import("@website-builder/p2p-collab");
        const parsed = p2p.parseRoomLink(location.href);
        if (!parsed || disposed) return;

        const keys = await p2p.deriveRoomKeys(parsed.key);
        const node = await p2p.createCollabNode({
          relayMultiaddr: cfg.relayMultiaddr,
        });
        if (disposed) {
          await node.stop();
          return;
        }

        // Membership handshake creds for the direct streams (Finding 5).
        const auth = { roomId: parsed.roomId, authKey: keys.authKey };

        const provider = new p2p.Libp2pYjsProvider(
          ydoc,
          p2p.libp2pRoomTransport(node, `${keys.topic}/yjs`, auth),
          keys,
          // Surface the room peer count for the "backed up vs. alone" UX (R7).
          { onPeersChanged: (n) => setRoomPeerCount(n) },
        );

        // Blob exchange shares the SAME OPFS bucket opfsAssetStore uses, so a
        // peer-fetched image lands where ImageWidget reads it.
        const { OpfsBlobStore } =
          await import("@website-builder/blob-store/opfs");
        const blobEx = new p2p.BlobExchange(
          new OpfsBlobStore(["sites", siteId]),
          p2p.libp2pBlobTransport(node, `${keys.topic}/blob`, auth),
          keys,
          {
            // Free-mode durability (D1): cache room images others announce (so
            // an image survives its uploader leaving), and mark a locally-added
            // image "backed up" the moment a peer takes a copy.
            replicateOnHave: true,
            onServed: (sha) => markBackedUp(sha),
          },
        );
        registerCollabBlobBridge(siteId, {
          get: (sha) => blobEx.get(sha),
          announce: (sha) => blobEx.announce(sha),
          probe: (sha) => blobEx.probeHolders(sha),
        });

        // Find + dial the other room members (the relay only gives reservations;
        // without this peers never connect to each other → no sync).
        const discovery = p2p.startRoomDiscovery(node, keys);

        teardown = () => {
          unregisterCollabBlobBridge(siteId);
          resetBackupStatus(); // don't leak per-sha status across sessions (R6)
          setRoomPeerCount(0); // collab ended → no peers
          discovery.stop();
          void blobEx.destroy().catch(() => {});
          void provider.destroy().catch(() => {}); // detaches listener + stops node
        };
        if (disposed) teardown();
      } catch (err) {
        console.warn("[editor] P2P collaboration unavailable:", err);
      }
    })();

    return () => {
      disposed = true;
      teardown?.();
    };
  }, [siteId, collabEpoch]);

  // OPFS persistence (M4) with CROSS-TAB, VERSION-AWARE LEADER ELECTION. OPFS
  // sync-access handles are exclusive per file across the whole origin, so only
  // ONE tab may open the SQLite DB. LeaderCoordinator elects a single leader
  // (Web Locks) that owns OPFS — replays + persists (incl. followers' edits,
  // which arrive via BroadcastChannel); every other tab is a FOLLOWER that never
  // touches OPFS and bootstraps its doc from a peer. The leader yields to any
  // strictly-NEWER app version, so the freshest code owns persistence (+ future
  // schema migrations). No Web Locks / no OPFS → best-effort single owner and
  // edits still sync + publish.
  useEffect(() => {
    const { ydoc } = docRef.current!;
    setLoaded(false);
    setCanSeed(false);

    const supported =
      typeof Worker !== "undefined" &&
      typeof navigator !== "undefined" &&
      typeof navigator.storage?.getDirectory === "function" &&
      typeof SharedArrayBuffer !== "undefined";
    if (!supported) {
      // No persistence → sole owner MUST seed a new site — UNLESS it loaded into
      // an active collab room, where it's a joiner that must wait for the room's
      // content instead of seeding a duplicate default (see the collab-seed guard
      // in onLead below for the full rationale).
      setCanSeed(getCollabConfig() == null);
      setLoaded(true);
      return;
    }

    let disposed = false;

    // Assume leadership: open OPFS + persist. Only ever runs on the tab holding
    // the lock. Idempotent (guards against a duplicate lead).
    const onLead = async (): Promise<void> => {
      if (disposed || persistRef.current) return;
      try {
        const { SqliteProvider, SahPoolBackend } =
          await import("@fredericrous/yjs-sqlite-provider");
        if (disposed) return;
        const provider = new SqliteProvider(ydoc, new SahPoolBackend(), {
          onPersist: () => wsRef.current?.announcePersisted(),
        });
        await provider.connect(`builder-site-${siteId}.sqlite3`);
        if (disposed) {
          void provider.destroy();
          return;
        }
        persistRef.current = provider;
        // This tab now answers for durability on the bridge.
        wsRef.current?.announcePersisted();
        // Replay done → push the authoritative doc to any followers that queried
        // before we opened OPFS (the leader no longer re-broadcasts its replay
        // update-by-update — see broadcastProvider). No-op with no peers.
        providerRef.current?.broadcastState();
      } catch (err) {
        console.warn(
          "[editor] local persistence unavailable; edits won't be saved across reloads:",
          err,
        );
      } finally {
        if (!disposed) {
          clearTimeout(followerFallback);
          // Authoritative leader (replay done, or degraded to no-persistence but
          // still the lock holder) → may seed a NEW-site default — UNLESS this
          // tab loaded into an active collab room. A fresh-origin joiner is its
          // OWN persistence leader, so without this guard it would seed a default
          // sitemap that then merges into the shared room over P2P → every
          // header/footer/section doubles (the duplicate-sections bug). The
          // originator's doc is already non-empty so it never seeds regardless;
          // a joiner must instead wait for the room's content via collab sync.
          setCanSeed(getCollabConfig() == null);
          setLoaded(true);
        }
      }
    };

    // Resign leadership: close OPFS (flush first) BEFORE the lock is released,
    // so the next/newer leader never opens OPFS while we still hold it.
    const onResign = async (): Promise<void> => {
      // No longer authoritative — a newer/other tab takes over persistence.
      setCanSeed(false);
      const p = persistRef.current;
      persistRef.current = null;
      await p?.destroy();
    };

    const coordinator = new LeaderCoordinator(siteId, version, {
      onLead,
      onResign,
    });
    coordinator.start();

    // Best-effort durability on abrupt close: React cleanup does NOT run when a
    // tab is closed/navigated away, so the leader's last queued writes could sit
    // in the write queue. `pagehide` fires on unload + bfcache; kick a flush so
    // pending updates are handed to the worker. Can't await here (the page is
    // going away) — this only tightens the small window; the real safety net is
    // the next leader reconciling storage up to the doc on takeover (F1).
    const onPageHide = (): void => {
      void persistRef.current?.flush();
    };
    window.addEventListener("pagehide", onPageHide);

    // Follower unblock + stuck-follower SELF-HEAL. Normally a follower's doc is
    // loaded once the leader answers its BroadcastChannel query (onSynced →
    // loaded). But `onLead` clears this timer, so if it fires we never acquired
    // the persist lock — another context holds it. A LIVE leader answers within
    // this window and syncs its state; if the doc is STILL empty here, the
    // holder is DEFUNCT — classically a bfcache/zombie page that kept the Web
    // Lock after freeze, whose `waitToLead()` blocks forever with no one ever
    // broadcasting. Without recovery the canvas stays PERMANENTLY blank (0
    // nodes, since readInitialSitemap won't seed while canSeed is false). So
    // promote to seed. Gated hard: only when the doc is EMPTY (never clobber
    // content a live leader already synced) and NOT in a collab room (a joiner
    // must wait for P2P sync, not seed a duplicate — see the collab guard
    // above). The jitter de-syncs two simultaneously-stuck followers so the
    // first to seed broadcasts before the second checks — avoiding a double
    // default (only one wins; the loser sees a non-empty doc and skips).
    const followerFallback = setTimeout(
      () => {
        if (disposed) return;
        setLoaded(true);
        if (ydoc.getMap("nodes").size === 0 && getCollabConfig() == null) {
          setCanSeed(true);
        }
      },
      3000 + Math.floor(Math.random() * 400),
    );

    return () => {
      disposed = true;
      clearTimeout(followerFallback);
      window.removeEventListener("pagehide", onPageHide);
      coordinator.destroy(); // release lock / cancel handover request
      const p = persistRef.current;
      persistRef.current = null;
      void p?.destroy(); // covers the no-Web-Locks path (coordinator won't resign)
    };
    // `version` is a fresh object each render; its two fields are the identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId, version.semver, version.build]);

  // An input moved into a section (a form) whose inputs already use its name
  // is renamed, as a drop or a duplicate would have named it.
  useEffect(() => renameClashingMovedInputs(), []);

  // Cmd+Z / Cmd+Shift+Z global handlers (T3.4). Pending settings edits are
  // flushed first, so undo removes what was just typed and no debounce timer
  // can write after it; each flush is an undo boundary.
  useEffect(() => {
    const { undoManager } = docRef.current!;
    const unregister = setUndoBoundary(() => undoManager.stopCapturing());
    const handler = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey)) return;
      if (e.key === "z" && !e.shiftKey) {
        e.preventDefault();
        flushPendingEdits();
        undoManager.undo();
      } else if ((e.key === "z" && e.shiftKey) || e.key === "y") {
        e.preventDefault();
        flushPendingEdits();
        undoManager.redo();
      }
    };
    document.addEventListener("keydown", handler);
    return () => {
      document.removeEventListener("keydown", handler);
      unregister();
    };
  }, []);

  return { ...docRef.current, loaded, canSeed };
}
